import { useQuery } from '@tanstack/react-query';
import {
  NotebookPen,
  X,
  AlertCircle,
  ShieldCheck,
  Info,
  RefreshCw,
  Package,
  Clock,
  TrendingUp,
  Zap,
  CheckCircle2,
} from 'lucide-react';
import { memo, useMemo, useCallback, Suspense, useEffect } from 'react';

import { Badge } from '@/components/ui/badge';
import { Button } from '@/components/ui/button';
import {
  Dialog,
  DialogContent,
  DialogTitle,
  DialogDescription,
  DialogHeader,
} from '@/components/ui/dialog';
import { GetItemUnderRequest } from '@/pages/PurchasesContainer/PurchasesRequests/api/PurchasesRequests.api';
import type {
  IOneItemInPrepareForRequest,
  ItemUnderRequest,
} from '@/pages/PurchasesContainer/PurchasesRequests/api/Request.interface.ts';

import UnderRequestTable from './Table';

type Props = {
  isOpen: boolean;
  closeFn: () => void;
  item: IOneItemInPrepareForRequest;
};

// Professional loading component with advanced effects
const LoadingIndicator = memo(() => (
  <div className="flex h-[60vh] min-h-[16rem] w-full flex-col items-center justify-center px-8 py-12">
    <div className="relative mb-8">
      {/* Multi-layered background effects */}
      <div className="absolute -inset-8 animate-pulse rounded-full bg-gradient-to-r from-blue-100/40 via-purple-100/40 to-blue-100/40 blur-2xl"></div>
      <div className="absolute -inset-6 animate-pulse rounded-full bg-gradient-to-r from-blue-200/30 via-purple-200/30 to-blue-200/30 blur-xl"></div>

      {/* Outer orbital ring */}
      <div className="absolute -inset-4 rounded-full border border-blue-200/30 dark:border-blue-800/30"></div>
      <div
        className="absolute -inset-4 animate-spin rounded-full border-2 border-transparent border-r-purple-500 border-t-blue-500"
        style={{ animationDuration: '4s' }}
      ></div>

      {/* Middle orbital ring */}
      <div
        className="absolute -inset-2 animate-spin rounded-full border-2 border-transparent border-b-blue-400 border-l-purple-400"
        style={{ animationDuration: '3s', animationDirection: 'reverse' }}
      ></div>

      {/* Inner orbital ring */}
      <div
        className="absolute -inset-1 animate-spin rounded-full border border-transparent border-t-blue-300"
        style={{ animationDuration: '2s' }}
      ></div>

      {/* Central icon container */}
      <div className="relative flex h-24 w-24 items-center justify-center overflow-hidden rounded-full bg-gradient-to-br from-white via-blue-50 to-purple-50 shadow-2xl ring-1 ring-blue-200/50 dark:from-blue-950 dark:via-blue-900 dark:to-purple-950 dark:ring-blue-800/50">
        <div className="absolute inset-0 rounded-full bg-gradient-to-br from-blue-500/10 to-purple-500/10"></div>
        <Zap className="relative h-12 w-12 text-blue-600 drop-shadow-sm dark:text-blue-400" />
      </div>

      {/* Floating particles */}
      <div className="absolute -right-2 -top-2 h-3 w-3 animate-ping rounded-full bg-blue-500 opacity-75"></div>
      <div
        className="absolute -bottom-2 -left-2 h-3 w-3 animate-ping rounded-full bg-purple-500 opacity-75"
        style={{ animationDelay: '0.5s' }}
      ></div>
      <div
        className="absolute -left-3 top-1/2 h-2 w-2 animate-ping rounded-full bg-blue-400 opacity-75"
        style={{ animationDelay: '1s' }}
      ></div>
      <div
        className="absolute -right-3 top-1/3 h-2 w-2 animate-ping rounded-full bg-purple-400 opacity-75"
        style={{ animationDelay: '1.5s' }}
      ></div>
    </div>

    {/* Enhanced text with animation */}
    <div className="space-y-4 text-center">
      <div className="space-y-2">
        <h3 className="bg-gradient-to-r from-blue-600 to-purple-600 bg-clip-text text-2xl font-bold text-transparent">
          جاري تحميل الطلبات
        </h3>
        <p className="max-w-sm text-sm text-gray-500 dark:text-gray-400">
          يتم الآن جلب وتحليل البيانات الخاصة بالطلبات النشطة
        </p>
      </div>

      {/* Animated progress dots */}
      <div className="flex items-center justify-center gap-2">
        <div
          className="h-2 w-2 animate-bounce rounded-full bg-blue-600"
          style={{ animationDelay: '0s' }}
        ></div>
        <div
          className="h-2 w-2 animate-bounce rounded-full bg-blue-500"
          style={{ animationDelay: '0.2s' }}
        ></div>
        <div
          className="h-2 w-2 animate-bounce rounded-full bg-purple-500"
          style={{ animationDelay: '0.4s' }}
        ></div>
        <div
          className="h-2 w-2 animate-bounce rounded-full bg-purple-600"
          style={{ animationDelay: '0.6s' }}
        ></div>
      </div>
    </div>
  </div>
));

LoadingIndicator.displayName = 'LoadingIndicator';

function UnderRequestDialog({ closeFn, isOpen, item }: Props) {
  // Extract the HealthyItemID from the item and ensure it's a number
  const healthyItemID = item?.HealthyItemID ?? 0;
  const hasValidItemID = healthyItemID > 0;

  // Query for fetching under request data
  const {
    data: underRequests,
    isLoading,
    isError,
    refetch,
    isFetching,
  } = useQuery({
    queryKey: ['itemUnderRequests', healthyItemID],
    queryFn: async (): Promise<ItemUnderRequest[]> => {
      try {
        const data = await GetItemUnderRequest(healthyItemID);
        return data || [];
      } catch (error) {
        console.error('Error fetching under requests:', error);
        throw error;
      }
    },
    enabled: isOpen && hasValidItemID,
    staleTime: 5 * 60 * 1000, // Consider data fresh for 5 minutes
    gcTime: 10 * 60 * 1000, // Keep in cache for 10 minutes
    refetchOnWindowFocus: false,
    retry: 1,
    placeholderData: [],
  });

  // Handle manual refresh
  const handleRefresh = useCallback(() => {
    refetch();
  }, [refetch]);

  // Derived state
  const hasData = useMemo(
    () => underRequests && underRequests.length > 0,
    [underRequests],
  );

  // Dialog close handler
  const handleDialogChange = useCallback(
    (open: boolean) => {
      if (!open) closeFn();
    },
    [closeFn],
  );

  // Add debug logs
  useEffect(() => {
    if (underRequests) {
      if (underRequests.length > 0) {
      }
    }
    if (isError) {
      console.error('UnderRequestsDialog - Error fetching data:', isError);
    }
  }, [underRequests, isError]);

  // Render main content based on loading state
  const renderMainContent = useCallback(() => {
    if (isLoading || isFetching) {
      return <LoadingIndicator />;
    }

    if (isError) {
      return (
        <div className="flex flex-col items-center justify-center px-8 py-16 text-center">
          <div className="relative mb-8">
            {/* Sophisticated background effects */}
            <div className="absolute -inset-8 animate-pulse rounded-full bg-gradient-to-r from-red-100/50 via-orange-100/50 to-red-100/50 blur-2xl"></div>
            <div className="absolute -inset-6 rounded-full bg-gradient-to-r from-red-200/30 via-orange-200/30 to-red-200/30 blur-xl"></div>

            {/* Icon container with enhanced styling */}
            <div className="relative flex h-28 w-28 items-center justify-center rounded-full bg-gradient-to-br from-red-50 via-orange-50 to-red-100 shadow-2xl ring-1 ring-red-200/50 dark:from-red-950/80 dark:via-orange-950/80 dark:to-red-900/80 dark:ring-red-800/50">
              <div className="absolute inset-0 rounded-full bg-gradient-to-br from-red-500/10 to-orange-500/10"></div>
              <AlertCircle className="relative h-16 w-16 text-red-600 drop-shadow-sm dark:text-red-400" />

              {/* Pulse effect */}
              <div
                className="absolute -inset-2 animate-pulse rounded-full border-2 border-red-300/50 dark:border-red-700/50"
                style={{ animationDuration: '3s' }}
              ></div>
            </div>
          </div>

          <div className="max-w-md space-y-6">
            <div className="space-y-3">
              <h3 className="text-2xl font-bold text-red-600 dark:text-red-400">
                حدث خطأ في التحميل
              </h3>
              <p className="leading-relaxed text-gray-600 dark:text-gray-400">
                تعذر تحميل البيانات في الوقت الحالي. يرجى التحقق من الاتصال
                والمحاولة مرة أخرى.
              </p>
            </div>

            <div className="flex justify-center gap-4">
              <Button
                variant="outline"
                className="group border-red-200 text-red-600 transition-all duration-300 hover:border-red-300 hover:bg-red-50 dark:border-red-800/50 dark:text-red-400 dark:hover:bg-red-900/20"
                onClick={closeFn}
              >
                <X className="mr-2 h-4 w-4 transition-transform group-hover:rotate-90" />
                إغلاق
              </Button>
              <Button
                variant="default"
                className="group bg-gradient-to-r from-blue-600 to-blue-700 text-white shadow-lg transition-all duration-300 hover:from-blue-700 hover:to-blue-800 hover:shadow-xl"
                onClick={handleRefresh}
              >
                <RefreshCw className="mr-2 h-4 w-4 transition-transform group-hover:rotate-180" />
                إعادة المحاولة
              </Button>
            </div>
          </div>
        </div>
      );
    }

    if (!hasValidItemID) {
      return (
        <div className="flex flex-col items-center justify-center px-8 py-16 text-center">
          <div className="relative mb-8">
            {/* Enhanced background effects */}
            <div className="absolute -inset-8 animate-pulse rounded-full bg-gradient-to-r from-amber-100/50 via-yellow-100/50 to-amber-100/50 blur-2xl"></div>
            <div className="absolute -inset-6 rounded-full bg-gradient-to-r from-amber-200/30 via-yellow-200/30 to-amber-200/30 blur-xl"></div>

            {/* Icon container */}
            <div className="relative flex h-28 w-28 items-center justify-center rounded-full bg-gradient-to-br from-amber-50 via-yellow-50 to-amber-100 shadow-2xl ring-1 ring-amber-200/50 dark:from-amber-950/80 dark:via-yellow-950/80 dark:to-amber-900/80 dark:ring-amber-800/50">
              <div className="absolute inset-0 rounded-full bg-gradient-to-br from-amber-500/10 to-yellow-500/10"></div>
              <Package className="relative h-16 w-16 text-amber-600 drop-shadow-sm dark:text-amber-400" />

              {/* Rotating ring */}
              <div
                className="absolute -inset-3 animate-spin rounded-full border-2 border-transparent border-r-yellow-400 border-t-amber-400"
                style={{ animationDuration: '4s' }}
              ></div>
            </div>
          </div>

          <div className="max-w-lg space-y-6">
            <div className="space-y-3">
              <h3 className="text-2xl font-bold text-amber-600 dark:text-amber-400">
                الصنف غير مرتبط
              </h3>
              <p className="leading-relaxed text-gray-600 dark:text-gray-400">
                لا يمكن عرض الطلبات النشطة لأن هذا الصنف غير مرتبط بنظام هلثي.
                يرجى ربط الصنف أولاً لعرض الطلبات المتعلقة به.
              </p>
            </div>

            <div className="rounded-lg border border-amber-200 bg-amber-50 p-4 dark:border-amber-800/50 dark:bg-amber-950/30">
              <div className="flex items-start gap-3">
                <Info className="mt-0.5 h-5 w-5 flex-shrink-0 text-amber-600 dark:text-amber-400" />
                <div className="text-sm text-amber-700 dark:text-amber-300">
                  <p className="mb-1 font-medium">نصيحة:</p>
                  <p>
                    قم بربط الصنف من خلال قائمة الأصناف أو من خلال خيارات
                    التحرير.
                  </p>
                </div>
              </div>
            </div>

            <Button
              variant="outline"
              className="group border-amber-200 px-8 text-amber-600 transition-all duration-300 hover:border-amber-300 hover:bg-amber-50 dark:border-amber-800/50 dark:text-amber-400 dark:hover:bg-amber-900/20"
              onClick={closeFn}
            >
              <span>فهمت، إغلاق</span>
              <X className="ml-2 h-4 w-4 transition-transform duration-300 group-hover:rotate-90" />
            </Button>
          </div>
        </div>
      );
    }

    if (hasData) {
      return (
        <div className="h-full overflow-auto rounded-b-lg">
          <Suspense fallback={<LoadingIndicator />}>
            <UnderRequestTable data={underRequests || []} />
          </Suspense>
        </div>
      );
    }

    return (
      <div className="flex flex-col items-center justify-center px-8 py-16 text-center">
        <div className="relative mb-8">
          {/* Sophisticated background effects */}
          <div className="absolute -inset-8 animate-pulse rounded-full bg-gradient-to-r from-green-100/50 via-emerald-100/50 to-green-100/50 blur-2xl"></div>
          <div className="absolute -inset-6 rounded-full bg-gradient-to-r from-green-200/30 via-emerald-200/30 to-green-200/30 blur-xl"></div>

          {/* Icon container */}
          <div className="relative flex h-28 w-28 items-center justify-center rounded-full bg-gradient-to-br from-green-50 via-emerald-50 to-green-100 shadow-2xl ring-1 ring-green-200/50 dark:from-green-950/80 dark:via-emerald-950/80 dark:to-green-900/80 dark:ring-green-800/50">
            <div className="absolute inset-0 rounded-full bg-gradient-to-br from-green-500/10 to-emerald-500/10"></div>
            <ShieldCheck className="relative h-16 w-16 text-green-600 drop-shadow-sm dark:text-green-400" />

            {/* Success particles */}
            <div className="absolute -right-2 -top-2 h-3 w-3 animate-ping rounded-full bg-green-400 opacity-75"></div>
            <div
              className="absolute -bottom-2 -left-2 h-2 w-2 animate-ping rounded-full bg-emerald-400 opacity-75"
              style={{ animationDelay: '0.5s' }}
            ></div>
            <div
              className="absolute -left-3 top-1/4 h-2 w-2 animate-ping rounded-full bg-green-500 opacity-75"
              style={{ animationDelay: '1s' }}
            ></div>
          </div>
        </div>

        <div className="max-w-lg space-y-6">
          <div className="space-y-3">
            <h3 className="text-2xl font-bold text-green-600 dark:text-green-400">
              لا توجد طلبات نشطة
            </h3>
            <p className="leading-relaxed text-gray-600 dark:text-gray-400">
              هذا أمر جيد! لا توجد طلبات نشطة أو معلقة لهذا الصنف في الوقت
              الحالي. جميع الطلبات السابقة تم معالجتها بنجاح.
            </p>
          </div>

          <div className="rounded-lg border border-green-200 bg-green-50 p-4 dark:border-green-800/50 dark:bg-green-950/30">
            <div className="flex items-center gap-3">
              <div className="flex h-8 w-8 items-center justify-center rounded-full bg-green-100 dark:bg-green-900/50">
                <TrendingUp className="h-4 w-4 text-green-600 dark:text-green-400" />
              </div>
              <div className="text-sm text-green-700 dark:text-green-300">
                <p className="font-medium">حالة ممتازة</p>
                <p className="text-xs opacity-90">جميع الطلبات محدثة ومعالجة</p>
              </div>
            </div>
          </div>

          <div className="flex justify-center gap-3">
            <Button
              variant="outline"
              className="group border-green-200 text-green-600 transition-all duration-300 hover:border-green-300 hover:bg-green-50 dark:border-green-800/50 dark:text-green-400 dark:hover:bg-green-900/20"
              onClick={handleRefresh}
            >
              <RefreshCw className="mr-2 h-4 w-4 transition-transform group-hover:rotate-180" />
              تحديث
            </Button>
            <Button
              variant="default"
              className="group bg-gradient-to-r from-green-600 to-emerald-600 text-white shadow-lg transition-all duration-300 hover:from-green-700 hover:to-emerald-700 hover:shadow-xl"
              onClick={closeFn}
            >
              <CheckCircle2 className="mr-2 h-4 w-4" />
              تم، إغلاق
            </Button>
          </div>
        </div>
      </div>
    );
  }, [
    isLoading,
    isFetching,
    isError,
    hasValidItemID,
    hasData,
    underRequests,
    closeFn,
    handleRefresh,
  ]);

  // Use isOpen directly to avoid additional re-renders
  if (!isOpen) return null;

  return (
    <Dialog open={isOpen} onOpenChange={handleDialogChange}>
      <DialogContent
        className="flex w-[95vw] max-w-7xl flex-col gap-0 overflow-hidden bg-gradient-to-br from-slate-50 to-gray-100 p-0 dark:from-slate-900 dark:to-gray-900"
        overlayClass="backdrop-blur-sm bg-black/20"
      >
        {/* Compact Header */}
        <DialogHeader className="border-b border-slate-200 bg-white dark:border-slate-700 dark:bg-slate-900">
          <div className="flex items-center justify-between px-4 py-3">
            {/* Left section - Title and item info */}
            <div className="flex items-center gap-3">
              <div className="flex h-9 w-9 items-center justify-center rounded-lg bg-slate-100 dark:bg-slate-800">
                <NotebookPen className="h-5 w-5 text-slate-600 dark:text-slate-400" />
              </div>

              <div className="space-y-0.5">
                <DialogTitle className="text-lg font-semibold text-slate-900 dark:text-slate-100">
                  الطلبات تحت الإنشاء
                </DialogTitle>
                <DialogDescription className="text-sm text-slate-600 dark:text-slate-400">
                  {item?.itemName || 'غير محدد'}
                </DialogDescription>
              </div>
            </div>

            {/* Center section - Status badges */}
            <div className="flex items-center gap-3">
              <Badge
                variant="outline"
                className="border-slate-300 bg-slate-50 text-slate-700 dark:border-slate-600 dark:bg-slate-800 dark:text-slate-300"
              >
                #{item?.HealthyItemID || '---'}
              </Badge>

              {hasData && (
                <Badge
                  variant="soft"
                  className="border-emerald-200 bg-emerald-50 text-emerald-700 dark:border-emerald-800 dark:bg-emerald-950 dark:text-emerald-300"
                >
                  {underRequests?.length || 0} طلب
                </Badge>
              )}
            </div>

            {/* Right section - Action buttons */}
            <div className="flex items-center gap-2">
              <Button
                variant="ghost"
                size="sm"
                onClick={handleRefresh}
                disabled={isLoading || isFetching}
                className="h-8 px-3 text-slate-600 hover:bg-slate-100 hover:text-slate-900 dark:text-slate-400 dark:hover:bg-slate-800 dark:hover:text-slate-100"
              >
                <RefreshCw
                  className={`h-4 w-4 ${isLoading || isFetching ? 'animate-spin' : ''}`}
                />
              </Button>

              <Button
                variant="ghost"
                size="sm"
                onClick={closeFn}
                className="h-8 w-8 p-0 text-slate-600 hover:bg-red-50 hover:text-red-600 dark:text-slate-400 dark:hover:bg-red-950 dark:hover:text-red-400"
              >
                <X className="h-4 w-4" />
              </Button>
            </div>
          </div>
        </DialogHeader>

        {/* Main Content Area */}
        <div className="flex-1 overflow-hidden bg-white shadow-inner dark:bg-slate-900">
          {hasData ? (
            <div className="flex h-full flex-col">
              {/* Data summary bar */}
              <div className="border-b border-slate-200 bg-gradient-to-r from-slate-50 to-gray-100 px-6 py-3 dark:border-slate-600 dark:from-slate-800 dark:to-slate-700">
                <div className="flex items-center justify-between">
                  <div className="flex items-center gap-6">
                    <div className="flex items-center gap-2">
                      <div className="h-2 w-2 animate-pulse rounded-full bg-green-500"></div>
                      <span className="text-sm font-medium text-slate-700 dark:text-slate-300">
                        {underRequests?.length || 0} طلب نشط
                      </span>
                    </div>
                    <div className="flex items-center gap-2">
                      <Clock className="h-4 w-4 text-slate-500" />
                      <span className="text-xs text-slate-500 dark:text-slate-400">
                        آخر تحديث: {new Date().toLocaleTimeString('ar-EG')}
                      </span>
                    </div>
                  </div>

                  <div className="flex items-center gap-2">
                    <TrendingUp className="h-4 w-4 text-green-500" />
                    <span className="text-sm font-medium text-green-600 dark:text-green-400">
                      حالة نشطة
                    </span>
                  </div>
                </div>
              </div>

              {/* Table container */}
              <div className="flex-1 overflow-auto">
                <Suspense fallback={<LoadingIndicator />}>
                  <UnderRequestTable data={underRequests || []} />
                </Suspense>
              </div>
            </div>
          ) : (
            renderMainContent()
          )}
        </div>
      </DialogContent>
    </Dialog>
  );
}

export default memo(UnderRequestDialog);
